import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, Send } from 'lucide-react';
import { useChat } from '../hooks/useChat';
import { useChatThreads, threadTitle } from '../hooks/useChatThreads';
import { useAuth } from '@/hooks/useAuth';
import { PageShell } from '@/components/layout/PageShell';
import { formatDate } from '@/lib/format';

export function ChatPanel() {
  const { threadId = '' } = useParams<{ threadId: string }>();
  const { user }          = useAuth();
  const { messages, loading, loadError, sending, sendError, sendMessage, markRead } = useChat(threadId);
  const { threads }       = useChatThreads();
  const thread            = threads.find((t) => t.thread_id === threadId);
  const title             = thread ? threadTitle(thread, user?.id) : 'Chat';
  const [input, setInput] = useState('');
  const bottomRef         = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    void markRead();
  }, [messages]);

  async function handleSend() {
    const content = input.trim();
    if (!content || sending) return;
    const ok = await sendMessage(content);
    if (ok) setInput('');
  }

  return (
    <PageShell title={title} back="/farms/conversas" maxWidth="md">
      <div className="card flex flex-col" style={{ height: 'calc(100vh - 260px)' }}>

        {/* Mensagens */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && (
            <p className="text-sm text-ink-muted text-center">Carregando mensagens…</p>
          )}
          {!loading && loadError && (
            <div className="flex items-start gap-2 rounded-xl bg-negative/10 border border-negative/20 px-3 py-2 text-xs text-negative">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>Erro ao carregar mensagens: {loadError}</span>
            </div>
          )}
          {!loading && !loadError && messages.length === 0 && (
            <p className="text-sm text-ink-muted text-center">Nenhuma mensagem ainda. Comece a conversa!</p>
          )}
          {messages.map((msg) => {
            const isMe = msg.sender_user_id === user?.id;
            return (
              <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                  isMe ? 'bg-brand-700 text-white rounded-tr-sm' : 'bg-surface-subtle text-ink rounded-tl-sm'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  <p className={`text-[11px] mt-1 ${isMe ? 'text-brand-200' : 'text-ink-soft'}`}>
                    {formatDate(msg.created_at)}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Erro de envio */}
        {sendError && (
          <div className="mx-3 mb-2 flex items-center gap-2 rounded-xl bg-negative/10 border border-negative/20 px-3 py-2 text-xs text-negative">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            {sendError}
          </div>
        )}

        {/* Input */}
        <div className="border-t border-border p-3 flex gap-2">
          <input
            type="text"
            className="input flex-1"
            placeholder="Digite uma mensagem…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
            }}
          />
          <button onClick={handleSend} disabled={sending || !input.trim()} className="btn-primary !p-2.5">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </PageShell>
  );
}
